import { useMemo } from 'react';
import type { FirestoreError } from 'firebase/firestore';
import type { Notification } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiGet, apiList } from '../../core/data/restClient';
import { markNotificationRead, markAllNotificationsRead } from '../../core/data/notificationMutations';

export interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: FirestoreError | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

interface NotificationsFeed {
  notifications: Notification[];
  loading: boolean;
  error: FirestoreError | null;
}

// A REST-serialised Firestore Timestamp arrives as {_seconds,_nanoseconds};
// tolerate a live Timestamp too. Used only for the client-side createdAt sort.
function tsMillis(t: unknown): number {
  const o = t as { _seconds?: number; seconds?: number; toMillis?: () => number };
  if (o && typeof o.toMillis === 'function') return o.toMillis();
  return (o?._seconds ?? o?.seconds ?? 0) * 1000;
}

/**
 * Read the current user's notification feed
 * (`notifications/{uid}/items`), sorted by `createdAt desc`.
 */
function useNotificationsFeed(uid: string | undefined): NotificationsFeed {
  const { rows, loading, error } = usePolledResource<Notification>(
    uid || null,
    () => apiList<Notification>(`/notifications/${uid}/items`),
  );
  // Server list has no orderBy — sort by createdAt desc.
  const notifications = useMemo(
    () => rows.slice().sort((a, b) => tsMillis(b.createdAt) - tsMillis(a.createdAt)),
    [rows],
  );
  return { notifications, loading, error: error as FirestoreError | null };
}

export function useNotifications(uid: string | undefined): UseNotificationsResult {
  const { notifications, loading, error } = useNotificationsFeed(uid);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = async (notificationId: string) => {
    if (!uid) return;
    await markNotificationRead(uid, notificationId);
  };

  const markAllAsRead = async () => {
    if (!uid) return;
    const unread = notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    await markAllNotificationsRead(uid, unread.map((n) => n.id));
  };

  return { notifications, unreadCount, loading, error, markAsRead, markAllAsRead };
}

/**
 * Lightweight hook that only returns the unread notifications count.
 * Used in TopBar to avoid loading the full notification list.
 */
export function useUnreadCount(uid: string | undefined): number {
  // Un count() côté serveur (1 lecture / 1 000) au lieu de relire tout le flux
  // sur chaque page (#66). usePolledResource veut un tableau : une ligne.
  const { rows } = usePolledResource<{ count: number }>(
    uid ? `unread:${uid}` : null,
    async () => [await apiGet<{ count: number }>(`/notifications/${uid}/items?unread=1&count=1`)],
  );
  return rows[0]?.count ?? 0;
}
